import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, RefreshControl, TouchableOpacity } from 'react-native';
import { useLocalSearchParams, router } from 'expo-router';
import Head from 'expo-router/head';
import { Check, Home, MapPin, Share2, Printer, ChevronRight } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme, SPACING, TYPOGRAPHY, CONTENT_MAX_WIDTH } from '../../src/theme';
import { ErrorState, EmptyState, BrandStrip } from '@components/common';
import { Header } from '@components/layout';
import { useFieldMode } from '@hooks/useFieldMode';
import {
  AddressHeader,
  OwnerStrip,
  PropertyTiles,
  RecentActivitySection,
  ActionsPanel,
  ClaimSheet,
  PropertyChecklistSection,
  ChecklistPreview,
  EvaluationInviteNote,
  ReportRecipientsSection,
  DefaultVisibilitySection,
  NotifyAgentSheet,
  EvaluationStatusPill,
  EvaluatorBanner,
  CoverageStrip,
  PlanSuggestions,
  StructureDetailSkeleton,
} from '@components/structure-detail';
import { brandFromChecklist } from '../../src/store/brandStore';
import type { InsuranceChecklist } from '../../src/types';
import { useStructuresStore } from '../../src/store/structuresStore';
import { useChecklistStore } from '../../src/store/checklistStore';
import { useUIStore } from '../../src/store/uiStore';
import { useAuthStore } from '../../src/store/authStore';
import { useAuthGateStore } from '../../src/store/authGateStore';
import { usePostsStore } from '../../src/store/postsStore';
import { UpgradeAccountSheet } from '@components/auth/UpgradeAccountSheet';
import { useStructureWithClaim } from '../../src/hooks/useStructureWithClaim';
import { suppressPairedBefores } from '../../src/utils/postPairing';
import { resolveStructureModelId } from '../../src/utils/structureModel';
import { shareStructure } from '@utils/share';
import { createPrefixedLogger } from '../../src/utils/logger';
import { StakeholderType } from '../../src/types';

const log = createPrefixedLogger('StructureDetail');

export default function StructureDetailScreen() {
  // `model` is the insurer deep link's checklist id (?model=), threaded here
  // by the Property tab / MapPathsOverlay via resolvePropertyHref. It
  // preselects the model in the claim sheet and drives the signed-out
  // preview; absent, both fall back to the recommended default. `item` is
  // the featured checklist's pre-expand target — set by "View checklist
  // item" links from a post's detail screen (and by the old
  // /structure/checklist/[id] redirect, which preserves it).
  const {
    id,
    model: modelParam,
    item,
    view,
  } = useLocalSearchParams<{
    id: string;
    model?: string;
    item?: string;
    view?: string;
  }>();
  // Only the Property tab threads ?model= through; the map sheet, search,
  // the picker and the profile lists all reach this screen bare, and the
  // page then showed the default model (FAIR Plan) under a State Farm
  // sidebar (Jason, Sept 7 2026). For the property the insurer link named,
  // the link's model is the intent, so fall back to it.
  const pendingProperty = useUIStore((s) => s.pendingProperty);
  const pendingModel = useUIStore((s) => s.pendingModel);
  const model = resolveStructureModelId({
    structureId: id,
    modelParam,
    pendingProperty,
    pendingModel,
  });
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  // This screen is public — it's the landing page for shared property links,
  // so signed-out visitors (e.g. an insurer sent a link) get a read-only
  // view instead of a redirect. Auth-required sections are gated on `user`.
  const { user } = useAuthStore();
  const fieldMode = useFieldMode();

  const { claimStructure, unclaimStructure, updateInsuranceAssignment, isClaiming } =
    useStructuresStore();

  const { structurePosts, fetchStructurePosts } = usePostsStore();
  // The store stamps structurePostsStructureId only when a fetch for that
  // structure completes, so this distinguishes "resolved (possibly empty)"
  // from "not fetched yet" — the visual audit waits on it before shooting.
  const structurePostsFor = usePostsStore((s) => s.structurePostsStructureId);
  const { structure, claim, claimResolved, isLoading, error } = useStructureWithClaim(id);
  // Render-level suppression of paired-away before posts (screen/selector
  // altitude, not the store fetch — pagination hasMore math stays based on
  // unfiltered page sizes). See postPairing.ts.
  const visibleStructurePosts = useMemo(
    () => suppressPairedBefores(structurePosts),
    [structurePosts]
  );

  const [refreshing, setRefreshing] = useState(false);
  const [claimSheetMode, setClaimSheetMode] = useState<'claim' | 'edit' | null>(null);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [previewChecklist, setPreviewChecklist] = useState<InsuranceChecklist | null | undefined>(
    undefined
  );
  const selectedChecklist = useChecklistStore((s) => s.selectedChecklist);
  const [notifySheetOpen, setNotifySheetOpen] = useState(false);
  // Anonymous Fire Scouts can't claim (a claim asserts "I am the insured"
  // and firestore.rules rejects anonymous creators), so the claim tap routes
  // through the save-your-account sheet first. Same pending-ref pattern as
  // AddPropertySheet: the effect below resumes the claim once `isAnonymous`
  // flips, and the uid is preserved by linkWithCredential so the claim
  // proceeds under the same identity.
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const pendingClaimAfterUpgradeRef = useRef(false);

  useEffect(() => {
    if (!pendingClaimAfterUpgradeRef.current || upgradeOpen) return;
    if (user && !user.isAnonymous) {
      pendingClaimAfterUpgradeRef.current = false;
      setClaimSheetMode('claim');
    }
  }, [user, upgradeOpen]);

  // Try-then-save: the visitor ticked items in the signed-out preview and
  // tapped its save CTA (previewEvaluation.saveRequested), then created an
  // account. Claim the structure with the previewed model and seed the ticks
  // as real progress. Gated on claimResolved so a claim doc that just hasn't
  // arrived yet can't be raced, and on saveRequested so a sign-in reached
  // any other way never claims a property the user didn't ask for.
  const autoClaimRanRef = useRef(false);
  useEffect(() => {
    if (autoClaimRanRef.current) return;
    if (!id || !user || user.isAnonymous || !claimResolved || claim) return;
    const preview = useUIStore.getState().previewEvaluation;
    if (!preview || preview.structureId !== id || !preview.saveRequested) return;
    autoClaimRanRef.current = true;
    (async () => {
      try {
        await claimStructure(id, user.id, {
          stakeholderType: 'homeowner',
          // Claims encode "default model" as an absent insurerChecklistId.
          insurerChecklistId: preview.checklistIsDefault ? undefined : preview.checklistId,
        });
      } catch (err) {
        // Claim failed (e.g. someone claimed it in the meantime). Keep the
        // preview state — the visitor can still claim manually, and that
        // path seeds the ticks too.
        log.warn('auto-claim after sign-up failed', err);
        return;
      }
      try {
        await useChecklistStore
          .getState()
          .applyPreviewTicks(id, preview.checklistId, preview.itemIds, preview.totalCount);
      } catch (err) {
        // Best-effort: the claim itself landed, the checklist just starts
        // empty. Don't strand the journey state over it.
        log.warn('seeding preview ticks failed', err);
      }
      useUIStore.getState().clearPropertyJourney(id);
    })();
  }, [id, user, claim, claimResolved, claimStructure]);
  // Desktop browsers usually lack navigator.share, so sharing falls back to
  // a clipboard copy — swap the icon to a checkmark briefly so the tap
  // visibly did something.
  const [shareCopied, setShareCopied] = useState(false);
  const shareCopiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (shareCopiedTimer.current) clearTimeout(shareCopiedTimer.current);
    },
    []
  );

  useEffect(() => {
    if (id) fetchStructurePosts(id);
  }, [id, fetchStructurePosts]);

  // Feed the Search tab's "recently viewed" list once this property loads.
  const recordStructureView = useUIStore((s) => s.recordStructureView);
  useEffect(() => {
    if (structure) recordStructureView(structure);
  }, [structure, recordStructureView]);

  const handleRefresh = async () => {
    if (!id) return;
    setRefreshing(true);
    try {
      await fetchStructurePosts(id);
    } finally {
      setRefreshing(false);
    }
  };

  const handleClaim = () => {
    if (!id) return;
    // Signed out → AuthGate. The pending ref + auth-state effect above resume
    // the claim once a signed-up user appears (same pattern as the map
    // sheet / AddPropertySheet). A Fire-Scout pick at the gate lands back
    // here as anonymous; the next tap routes through the upgrade sheet.
    if (!user) {
      pendingClaimAfterUpgradeRef.current = true;
      useAuthGateStore.getState().open(() => {}, {
        message: 'Sign in to claim a property',
        // Keep the insurer link's model through the sign-up round trip so
        // the claim sheet still preselects it on return.
        returnPath: model
          ? `/structure/${id}?model=${encodeURIComponent(model)}`
          : `/structure/${id}`,
      });
      return;
    }
    if (user.isAnonymous) {
      pendingClaimAfterUpgradeRef.current = true;
      setUpgradeOpen(true);
      return;
    }
    setClaimSheetMode('claim');
  };

  const handleEditInsurance = () => {
    if (!user || !id) return;
    setClaimSheetMode('edit');
  };

  const handleClaimSubmit = async ({
    stakeholderType,
    insurerChecklistId,
  }: {
    stakeholderType?: StakeholderType;
    insurerChecklistId?: string;
  }) => {
    if (!user || !id) return;
    setClaimError(null);
    // Keep the sheet open on failure and surface the message inside it — an
    // uncaught rejection here left the sheet up with no feedback at all.
    try {
      if (claimSheetMode === 'edit') {
        await updateInsuranceAssignment(id, user.id, {
          stakeholderType: stakeholderType ?? null,
          insurerChecklistId: insurerChecklistId ?? null,
        });
      } else {
        await claimStructure(id, user.id, { stakeholderType, insurerChecklistId });
      }
    } catch (err) {
      setClaimError(err instanceof Error ? err.message : 'Could not claim this property.');
      return;
    }
    // A fresh claim consumes any try-then-save preview held for this
    // structure: seed the ticks when the claimed model matches what was
    // previewed (fire-and-forget — applyPreviewTicks refreshes the store's
    // progress when it lands), and clear the journey state either way so the
    // Property tab stops routing at a stale pending link.
    if (claimSheetMode === 'claim') {
      const preview = useUIStore.getState().previewEvaluation;
      if (preview && preview.structureId === id) {
        const modelMatches =
          insurerChecklistId === preview.checklistId ||
          (insurerChecklistId === undefined && preview.checklistIsDefault);
        if (modelMatches && preview.itemIds.length > 0) {
          useChecklistStore
            .getState()
            .applyPreviewTicks(id, preview.checklistId, preview.itemIds, preview.totalCount)
            .catch((err) => log.warn('seeding preview ticks failed', err));
        }
      }
      useUIStore.getState().clearPropertyJourney(id);
    }
    setClaimSheetMode(null);
  };

  const handleUnclaim = async () => {
    if (!user || !id) return;
    await unclaimStructure(id, user.id);
  };

  if (isLoading) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <Header showBack title="Property Details" />
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
          <View style={styles.contentConstraint}>
            <StructureDetailSkeleton />
          </View>
        </ScrollView>
      </View>
    );
  }

  if (error) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <Header showBack title="Property Details" />
        <ErrorState title="Unable to load property" message={error} onRetry={() => router.back()} />
      </View>
    );
  }

  if (!structure) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <Header showBack title="Property Details" />
        <EmptyState
          icon={Home}
          title="Property not found"
          message="This property may have been removed or doesn't exist."
          actionLabel="Go Back"
          onAction={() => router.back()}
        />
      </View>
    );
  }

  const isClaimed = Boolean(claim);
  // Guard on both sides existing: with no user AND no claim the optional
  // chains both yield undefined and `undefined === undefined` would mark a
  // signed-out visitor as the owner.
  const isOwnedByUser = Boolean(user && claim && claim.claimedBy === user.id);

  // The strip above the address shows the brand of the model on screen, not
  // the app-wide link brand: a claimed State Farm home should not read
  // "with California FAIR Plan" because the visitor arrived by that link.
  // undefined = not resolved yet (BrandStrip falls back to the link brand).
  const viewedChecklist = user ? selectedChecklist : previewChecklist;
  const pageBrand = viewedChecklist == null ? undefined : brandFromChecklist(viewedChecklist);

  const actionsPanel = (
    <ActionsPanel
      isClaimed={isClaimed}
      isOwnedByUser={isOwnedByUser}
      structureId={id!}
      isClaiming={isClaiming}
      onClaim={handleClaim}
      onUnclaim={handleUnclaim}
      onEditInsurance={isOwnedByUser ? handleEditInsurance : undefined}
      onNotifyAgent={isOwnedByUser ? () => setNotifySheetOpen(true) : undefined}
    />
  );

  const handleShowOnMap = () => {
    if (!id) return;
    // Eager select so the sheet/panel opens the moment the map shows,
    // while the focus fetch is still in flight.
    if (structure) useStructuresStore.getState().selectStructure(structure);
    // Focus request goes through uiStore: dismissTo (POP_TO) returns to the
    // existing map without remounting it (or replaces this screen when
    // there's no history, e.g. a direct deep link), but it drops router
    // params on that path — so the map screen consumes the pending id and
    // does the pan/zoom to street level itself.
    useUIStore.getState().requestMapFocus(id);
    router.dismissTo('/');
  };

  const handleShare = async () => {
    if (!id) return;
    const result = await shareStructure({ structureId: id, address: structure?.address });
    if (result === 'copied') {
      useUIStore.getState().showToast({ message: 'Link copied' });
      setShareCopied(true);
      if (shareCopiedTimer.current) clearTimeout(shareCopiedTimer.current);
      shareCopiedTimer.current = setTimeout(() => setShareCopied(false), 2000);
    }
    if (result === 'failed') {
      useUIStore.getState().showToast({ message: "Couldn't copy the link" });
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <Head>
        <title>{`${structure.address || 'Property'} - Redi`}</title>
      </Head>
      {/* Icons-only top bar (mockup dethead) — AddressHeader's light address
          below carries the screen, so the title isn't set twice. */}
      <Header
        showBack
        secondRightIcon={shareCopied ? Check : Share2}
        onSecondRightPress={handleShare}
        secondRightLabel={shareCopied ? 'Link copied' : 'Share property link'}
        rightIcon={MapPin}
        onRightPress={handleShowOnMap}
        rightLabel="Show on map"
      />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}>
        <View style={[styles.contentConstraint, { paddingBottom: insets.bottom + 80 }]}>
          {/* (evaluator banner) → identity → coverage → invite → (claim CTA,
              unclaimed only) → checklist → ownership → (evaluation status,
              claimed) → (claim CTA, claimed) → context. The featured
              checklist is the reason to be here, so it leads once there's a
              claim to work; on an unclaimed property the claim CTA has to
              stay in the first screenful instead. */}
          <EvaluatorBanner visible={view === 'evaluator'} />

          <BrandStrip brand={pageBrand} />
          <AddressHeader structure={structure} isClaimed={isClaimed} />

          <CoverageStrip isOwnedByUser={isOwnedByUser} />

          <EvaluationInviteNote structureId={id!} hasModelParam={Boolean(model)} />

          {!isClaimed && actionsPanel}

          {/* Progress docs require auth in Firestore rules, but checklist
              definitions are public — signed-out visitors (insurer links)
              get the try-then-save preview instead of nothing. */}
          {user ? (
            <PropertyChecklistSection
              structureId={structure.id}
              claim={claim}
              canEdit={isOwnedByUser}
              linkModelId={model}
              initialItemId={item}
            />
          ) : (
            <ChecklistPreview
              structureId={structure.id}
              modelId={model}
              onChecklistResolved={setPreviewChecklist}
            />
          )}

          {isClaimed && claim && (
            <OwnerStrip
              isOwnedByUser={isOwnedByUser}
              ownerName={
                isOwnedByUser && user
                  ? `${user.firstName} ${user.lastName}`.trim() || undefined
                  : undefined
              }
              claimedAt={claim.claimedAt}
            />
          )}

          {isClaimed && claim?.evaluationStatus && (
            <EvaluationStatusPill status={claim.evaluationStatus} at={claim.evaluationStatusAt} />
          )}

          {isClaimed && actionsPanel}

          <PropertyTiles structure={structure} />

          {/* Field mode (phone website) leaves out the planned-work line. */}
          {!fieldMode && (
            <PlanSuggestions
              structureId={structure.id}
              latitude={structure.centroid.latitude}
              longitude={structure.centroid.longitude}
            />
          )}

          <RecentActivitySection
            structurePosts={visibleStructurePosts}
            postsCount={structure.postsCount}
            structureId={id!}
            postsResolved={structurePostsFor === id}
          />

          {isOwnedByUser && claim && (
            <DefaultVisibilitySection
              structureId={id!}
              defaultPostVisibility={claim.defaultPostVisibility}
            />
          )}

          {isOwnedByUser && claim && (
            <ReportRecipientsSection
              structureId={id!}
              initialRecipients={claim.reportRecipients ?? []}
            />
          )}

          {/* Printable version of the same report the owner can email. The
              browser's Print dialog is what turns it into a PDF for now. */}
          {isOwnedByUser && claim && (
            <TouchableOpacity
              style={[styles.reportLink, { borderTopColor: colors.border }]}
              onPress={() =>
                router.push({ pathname: '/structure/report/[id]', params: { id: id! } })
              }
              accessibilityRole="button"
              accessibilityLabel="View printable readiness report">
              <Printer size={16} color={colors.textSecondary} />
              <Text style={[styles.reportLinkText, { color: colors.text }]}>
                Printable readiness report
              </Text>
              <ChevronRight size={14} color={colors.textTertiary} />
            </TouchableOpacity>
          )}
        </View>
      </ScrollView>

      <UpgradeAccountSheet visible={upgradeOpen} onClose={() => setUpgradeOpen(false)} />

      <ClaimSheet
        visible={claimSheetMode !== null}
        title={claimSheetMode === 'edit' ? 'Update preparation model' : 'Claim this structure'}
        submitLabel={claimSheetMode === 'edit' ? 'Save' : 'Claim this structure'}
        initialStakeholderType={claim?.stakeholderType}
        // The insurer link's ?model= preselects the model on a fresh claim;
        // an existing claim's own assignment always wins in edit mode.
        initialChecklistId={claim ? claim.insurerChecklistId : model}
        autoSelectRecommended={claimSheetMode === 'claim'}
        isSubmitting={isClaiming}
        errorMessage={claimError}
        onClose={() => {
          setClaimError(null);
          setClaimSheetMode(null);
        }}
        onSubmit={handleClaimSubmit}
      />

      <NotifyAgentSheet
        open={notifySheetOpen}
        structureId={id!}
        currentStatus={claim?.evaluationStatus}
        onClose={() => setNotifySheetOpen(false)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  reportLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    paddingVertical: SPACING.md,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  reportLinkText: {
    ...TYPOGRAPHY.body,
    flex: 1,
  },
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    alignItems: 'center',
  },
  contentConstraint: {
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    padding: SPACING.md,
    gap: SPACING.md,
  },
});
